import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import ListIcon from '@mui/icons-material/List';
import AddIcon from '@mui/icons-material/Add';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';
import RoomPropertiesListRow from 'components/RoomPropertiesListRow';
import RoomPropertiesListAdd from 'components/RoomPropertiesListAdd';

import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';
import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';

import Query, {
  RoomPropertiesListRefetchQuery,
} from '__generated__/RoomPropertiesListRefetchQuery.graphql';
import { RoomPropertiesList_data$key } from '__generated__/RoomPropertiesList_data.graphql';

type Props = {
  data: RoomPropertiesList_data$key;
};

const RoomPropertiesList: FC<Props> = ({ data }) => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [{ roomProperties }, { refetch, loading }] = useAvoidSuspense<
    RoomPropertiesListRefetchQuery,
    RoomPropertiesList_data$key
  >(
    graphql`
      fragment RoomPropertiesList_data on Query
      @refetchable(queryName: "RoomPropertiesListRefetchQuery") {
        roomProperties(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id

              ...RoomPropertiesListRow_roomProperty
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    data,
    Query
  );

  const connectionId = roomProperties?.__id;

  const [deleteRoomProperty, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = roomProperties ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<ListIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableCell />
            <TableHeadCell name="name">Pavadinimas</TableHeadCell>
            <TableHeadCell center name="etgKey">
              ETG ID
            </TableHeadCell>
            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <RoomPropertiesListRow
            key={node.id}
            roomProperty={node}
            onDelete={deleteRoomProperty}
          />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm" icon={<AddIcon />}>
          <RoomPropertiesListAdd connectionId={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default RoomPropertiesList;
